// Auto-pause (look-away / city under attack) and the milestone cinematics that
// pause through the same controller. Extracted from App.tsx (ROADMAP 7.14); the
// hook order is unchanged.

import { useCinematicDirector } from '../cinematics/useCinematicDirector.js';
import type { GameMode } from '../multiplayer/lobby/shouldConnectMpLobby.js';
import type { PauseOnDanger } from '../pause/detectDangers.js';
import { usePauseController } from '../pause/usePauseController.js';
import { useSettings } from '../settings/useSettings.js';
import type { humanAssistsOf } from '../setup/setupModel.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';

/** The preset's pause-on-danger for the current match (see App's startFromSetup). */
export interface PauseOverride {
  value: PauseOnDanger;
  settingAtStart: PauseOnDanger;
}

/** Auto-pause when the player looks away or their cities are hit (single-player only; pacing spec §4). */
export function usePauseWiring(
  sim: SimWorld,
  gameMode: GameMode,
  pauseOverride: PauseOverride | null,
  localPlayerIndex: number,
) {
  const autoPauseWhenAway = useSettings((s) => s.autoPauseWhenAway);
  const pauseSetting = useSettings((s) => s.pauseOnDanger);
  const pauseOnDanger =
    pauseOverride && pauseSetting === pauseOverride.settingAtStart
      ? pauseOverride.value
      : pauseSetting;
  return usePauseController({
    enabled: gameMode === 'single' && sim.victory === null,
    runState: sim.runState,
    pause: sim.pause,
    resume: sim.resume,
    snapshot: sim.snapshot,
    tick: Number(sim.tick ?? 0),
    localPlayer: localPlayerIndex,
    autoPauseWhenAway,
    pauseOnDanger,
  });
}

/**
 * Milestone cinematics (pacing spec §6 C2): `milestone:reached` events play
 * a short, skippable moment. Single-player pauses through the controller's
 * 'cinematic' claim, so a game the player paused stays paused. The seat's
 * ui_mode (kid layer) isn't exposed by the match setup on main yet, so it
 * defaults to 'standard'; window.__aod.testMilestone(p, { uiMode }) can
 * override it.
 */
export function useMatchCinematics(
  sim: SimWorld,
  gameMode: GameMode,
  pauseController: ReturnType<typeof usePauseController>,
  humanAssists: ReturnType<typeof humanAssistsOf>,
  localPlayerIndex: number,
) {
  const cinematics = useCinematicDirector({
    mode: 'single',
    enabled: gameMode === 'single',
    events: sim.events ?? [],
    tick: Number(sim.tick ?? 0),
    snapshot: sim.snapshot,
    localPlayer: localPlayerIndex,
    // Kid-friendly banners (icon + friendly line) for the icons UI mode.
    uiMode: humanAssists?.uiMode,
    pause: {
      pause: pauseController.cinematicPause,
      resume: pauseController.cinematicResume,
    },
  });
  // The finale hands over to VictoryBanner / PostVictorySummary when it ends.
  const finalePlaying =
    cinematics.state.active?.recipeId === 'victory.achieved' ||
    cinematics.state.active?.recipeId === 'victory.lost';
  return { cinematics, finalePlaying };
}
